import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import api, { mediaUrl } from '../lib/api';

const PostCard = ({ p, featured }) => (
  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
    className={`group block bg-white/70 rounded-3xl overflow-hidden border border-rose/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${featured ? 'md:col-span-2 md:grid md:grid-cols-2' : ''}`}>
    <SmartImage src={mediaUrl(p.cover_image)} alt={p.title} className={featured ? 'aspect-[16/10] md:h-full' : 'aspect-[4/3]'}>
      <div className="absolute top-3 left-3 z-10 rounded-full bg-cream/90 text-mahogany text-[11px] uppercase tracking-widest px-3 py-1">{p.category}</div>
    </SmartImage>
    <div className={`p-6 ${featured ? 'md:flex md:flex-col md:justify-center md:p-10' : ''}`}>
      <div className="text-xs text-mahogany/60 uppercase tracking-wider mb-2">{p.author}</div>
      <h3 className={`font-serif-display text-burgundy leading-tight mb-3 group-hover:text-rose-italic transition-colors ${featured ? 'text-3xl md:text-4xl' : 'text-2xl'}`}>{p.title}</h3>
      <p className="text-sm text-burgundy/80 line-clamp-3">{p.excerpt}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-mahogany font-medium text-sm">Read More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
    </div>
  </Link>
);

const BlogPage = () => {
  const [cat, setCat] = useState('All');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/blog/posts')
      .then((res) => setPosts(res.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(posts.map((p) => p.category)))], [posts]);
  const filtered = cat === 'All' ? posts : posts.filter((p) => p.category === cat);

  return (
    <div className="App" data-testid="page-blog">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-center">
            <div className="eyebrow mb-3">News &amp; Blogs</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-burgundy leading-tight">Words from the <span className="font-italic-display text-rose-italic">Path</span></h1>
            <p className="mt-5 text-mahogany/80 max-w-2xl mx-auto">Poems, stories and reflections on meditation, love and awareness from our community of seekers.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mt-8" data-testid="blog-categories">
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`blog-cat-${c.replace(/[^a-z]/gi, '-').toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${cat === c ? 'bg-mahogany text-cream border-mahogany' : 'bg-white/70 text-mahogany/80 border-rose/50 hover:bg-rose-soft/40'}`}>{c}</button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center mt-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
          ) : filtered.length === 0 ? (
            <div className="mt-16 text-center text-mahogany/60" data-testid="blog-empty">No posts yet. Please check back soon.</div>
          ) : (
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
              {filtered.map((p, i) => <PostCard key={p.slug} p={p} featured={cat === 'All' && i === 0} />)}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
